// src/PO-Process/POHome.jsx

import { Grid, Box, Autocomplete, TextField, Checkbox, ListItemText, Chip } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useEffect, useState } from "react";
import dayjs from "dayjs";

import { getAllPOData } from "./api/api";
import api from "../axiosConfig";

import { Toaster } from "../components/Toaster";
import LoadingSpinner from "../components/LoadingSpinner";
import Card, { CardContent } from "./components/ui/card";
import ContainedButton from "../components/ui/ContainedButton";
import OutlinedButton from "../components/ui/OutlinedButton";
import UploadPOModel from "./components/UploadPOModel";
import FilteredPO from "./components/FilteredPO";

const POHome = ({ open }) => {
  const rawFilterData = localStorage.getItem("filterData");
  const localFilterData =
    rawFilterData && rawFilterData !== "null"
      ? JSON.parse(rawFilterData)
      : null;

  const [filterData, setFilterData] = useState({
    startDate: localFilterData?.startDate ?? null,
    endDate: localFilterData?.endDate ?? null,
    selectedCustomer: localFilterData?.selectedCustomer ?? [],
    selectedDC: localFilterData?.selectedDC ?? [],
    selectedCity: localFilterData?.selectedCity ?? [],
  });

  const [customers, setCustomers] = useState([]);
  const [dcNames, setDcNames] = useState([]);
  const [cities, setCities] = useState([]);
  const [dcCityMap, setDcCityMap] = useState({});
  const [filteredPOList, setFilteredPOList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDcCityMap = async () => {
      try {
        const response = await api.get("/get-dc-city-map");
        setDcCityMap(response.data || {});
      } catch (error) {
        Toaster.error("Failed to fetch DC-City mapping");
      }
    };
    fetchDcCityMap();
  }, []);

  const isAllSelected = (selected, options) =>
    selected.length === options.length;

  const renderMultiSelect = ({
    label,
    options,
    selectedValues,
    onChange,
    placeholder,
  }) => {
    const displayLimit = 2;

    const allOptions = ["Select All", ...options];

    return (
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={allOptions}
        value={selectedValues}
        onChange={(event, newValue) => {
          if (newValue.includes("Select All")) {
            const isAllSelected = selectedValues.length === options.length;
            onChange(isAllSelected ? [] : options);
          } else {
            onChange(newValue.filter((val) => val !== "Select All"));
          }
        }}
        getOptionLabel={(option) => option}
        isOptionEqualToValue={(option, value) => option === value}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              style={{ marginRight: 8 }}
              checked={
                option === "Select All"
                  ? selectedValues.length === options.length
                  : selected
              }
            />
            <ListItemText
              primary={
                option === "Select All" ? `Select All` : option
              }
            />
          </li>
        )}
        renderTags={(selected) => {
          const visibleLimit = selected.length > 2 ? 1 : 2;
          const visible = selected.slice(0, visibleLimit);
          const remaining = selected.length - visible.length;
          
          return (
            <>
              {visible.map((value) => (
                <Box key={value} component="span" sx={{ mr: 0.5 }}>
                  <Chip
                    label={value}
                    size="small"
                    sx={{
                      height: 24,
                      maxWidth: 120,
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  />
                </Box>
              ))}
              {remaining > 0 && (
                <Chip
                  label={`+${remaining}`}
                  size="small"
                  sx={{ height: 24 }}
                />
              )}
            </>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            size="small"
            variant="outlined"
            sx={{
              '& .MuiInputBase-root': {
                height: 40
              }
            }}
          />
        )}
        sx={{
          width: '100%',
          maxWidth: 280,
          '.MuiAutocomplete-tag': { display: 'none' }, // hide default chips
          '.MuiInputBase-root': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minHeight: '40px',
          },
        }}
      />
    );
  };

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await api.get("/get-city-list");
        setCities(response.data || []);
      } catch (error) {
        Toaster.error("Failed to fetch cities");
      }
    };
    fetchCities();
  }, []);

  // Fetch DC Names
  useEffect(() => {
    const fetchDCNames = async () => {
      try {
        const response = await api.get("/get-dc-name-list");
        setDcNames(response.data || []);
      } catch (error) {
        Toaster.error("Failed to fetch DC names");
      }
    };
    fetchDCNames();
  }, []);

  // Fetch Customer Names
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await api.get("/get-customers");
        setCustomers(response.data || []);
      } catch (error) {
        Toaster.error("Failed to fetch customers");
      }
    };
    fetchCustomers();
  }, []);

  useEffect(() => {
    // If filters already exist in localStorage, do NOTHING
    if (localFilterData) return;

    // Wait until data is loaded
    if (!customers.length || !cities.length || !Object.keys(dcCityMap).length) return;

    const startDate = dayjs().subtract(1, "day"); // yesterday
    const endDate = dayjs().add(6, "day");        // next 7 days

    const allCities = cities;
    const allCustomers = customers;

    // Auto-derive DCs from cities
    const allDCs = Object.keys(dcCityMap).filter(dc =>
      (dcCityMap[dc] || []).some(city => allCities.includes(city))
    );

    const defaultFilters = {
      startDate: startDate,
      endDate: endDate,
      selectedCustomer: allCustomers,
      selectedCity: allCities,
      selectedDC: allDCs,
    };

    // Set defaults in UI
    setFilterData(defaultFilters);

    // Save defaults so refresh keeps them
    localStorage.setItem("filterData", JSON.stringify(defaultFilters));

  }, [customers, cities, dcCityMap]);

  // Filter Apply Handler
  const handleApplyFilters = async () => {
    if (
      !filterData.startDate || 
      !filterData.endDate || 
      !filterData.selectedCustomer.length || 
      (!filterData.selectedDC.length && !filterData.selectedCity.length)
    ) {
      Toaster.warning("Please select all required filters.");
      return;
    }

    try {
      setLoading(true);
      localStorage.setItem("filterData", JSON.stringify(filterData));

      const response = await getAllPOData();
      const allFiles = response.files || [];

      const start = dayjs(filterData.startDate);
      const end = dayjs(filterData.endDate);

      const matchesFilter = (row) => {
        const poDate = row?.PO_Date ? dayjs(row.PO_Date) : null;
        const matchDate = poDate && start && end && poDate.isBetween(start, end, null, "[]");

        const matchCustomer = filterData.selectedCustomer.includes(row?.Customer);
        const matchDC = filterData.selectedDC.includes(row?.DC_Name);
        
        const matchCity = filterData.selectedCity.includes(row?.City); // assuming backend sends `City` field

        const allNull = !row?.PO_Date && !row?.Customer && !row?.DC_Name;
        const partialMatch = (matchDate || matchCustomer || matchDC)
          && (!row?.PO_Date || matchDate)
          && (!row?.Customer || matchCustomer)
          && (!row?.DC_Name || matchDC)
          && (!row?.City || matchCity);

        return allNull || partialMatch;
      };

      const filtered = allFiles.filter(matchesFilter);
      setFilteredPOList(filtered);
    } catch (error) {
      Toaster.error("Something went wrong while applying filters.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filterData.startDate && filterData.endDate) {
      handleApplyFilters();
    }
  }, [filterData.startDate, filterData.endDate]);

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="loading-spinner">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="ag-table-container" style={{ marginLeft: open ? "240px" : "0px" }}>
      {/* Filter Panel */}
      <Box sx={{ mb: 2 }}>
        <Card>
          <CardContent>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    columnGap: '12px',
                    rowGap: '12px',
                  }}
                >

                  <Box sx={{ flex: '1 1 280px', minWidth: 240, maxWidth: 280 }}>
                    {renderMultiSelect({
                      label: "City",
                      options: cities,
                      selectedValues: filterData.selectedCity,
                      onChange: (val) =>{
                        // Set selected Cities
                        let newFilterData = { ...filterData, selectedCity: val };

                        // Auto-select corresponding DCs
                        const autoDCs = Object.keys(dcCityMap).filter(dc =>
                          (dcCityMap[dc] || []).some(city => val.includes(city))
                        );
                        newFilterData.selectedDC = Array.from(new Set([...filterData.selectedDC, ...autoDCs]));

                        setFilterData(newFilterData);
                      },
                      placeholder: "Select City(s)",
                    })}
                  </Box>

                  <Box sx={{ flex: '1 1 280px', minWidth: 240, maxWidth: 280 }}>
                    {renderMultiSelect({
                      label: "DC Name",
                      options: dcNames,
                      selectedValues: filterData.selectedDC,
                      onChange: (val) => {
                        // Set selected DCs
                        let newFilterData = { ...filterData, selectedDC: val };

                        // Auto-select corresponding cities
                        const autoCities = val.flatMap(dc => dcCityMap[dc] || []);
                        newFilterData.selectedCity = Array.from(new Set([...filterData.selectedCity, ...autoCities]));

                        setFilterData(newFilterData);
                      },
                      placeholder: "Select DC(s)",
                    })}
                  </Box>

                  <Box sx={{ flex: '1 1 280px', minWidth: 240, maxWidth: 280 }}>
                    {renderMultiSelect({
                      label: "Customer",
                      options: customers,
                      selectedValues: filterData.selectedCustomer,
                      onChange: (val) =>
                        setFilterData((prev) => ({ ...prev, selectedCustomer: val })),
                      placeholder: "Select Customer(s)",
                    })}
                  </Box>

                  <Box sx={{ flex: '1 1 160px', minWidth: 150 }}>
                    <DatePicker
                      label="Start Date"
                      value={filterData?.startDate ? dayjs(filterData.startDate) : null}
                      onChange={(newValue) => setFilterData(prev => ({
                        ...prev, startDate: newValue
                      }))}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: { width: '100%' },
                        },
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 160px', minWidth: 150 }}>
                    <DatePicker
                      label="End Date"
                      value={filterData?.endDate ? dayjs(filterData.endDate) : null}
                      onChange={(newValue) => setFilterData(prev => ({
                        ...prev, endDate: newValue
                      }))}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: { width: '100%' },
                        },
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <OutlinedButton onClick={handleApplyFilters}>
                      Apply Filters
                    </OutlinedButton>

                    {localFilterData && (
                      <OutlinedButton
                        onClick={() => {
                          localStorage.removeItem("filterData");
                          setFilterData({
                            startDate: null,
                            endDate: null,
                            selectedCustomer: [],
                            selectedDC: [],
                            selectedCity: [],
                          });
                          setFilteredPOList([]);
                        }}
                      >
                        Clear Filters
                      </OutlinedButton>
                    )}

                    <ContainedButton onClick={() => setIsModalOpen(true)}>
                      Upload PO
                    </ContainedButton>
                  </Box>
                </Box>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </CardContent>
        </Card>
        <UploadPOModel
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          customers={customers}
        />
      </Box>

      {/* Filtered PO Table */}
      <Box>
        <FilteredPO
          key={filteredPOList.length} // force refresh if needed
          filteredPOList={filteredPOList}
          filterData={filterData}
          handleApplyFilters={handleApplyFilters}
        />
      </Box>
    </div>
  );
};

export default POHome;
